import * as XLSX from 'xlsx';
import type { SpecRow, CuttingDataRow, ParseResult } from './types';

// Columns A–T in Лист1 (0-based indices)
const COL = {
  NUMBER: 0,        // A - №
  ARTICLE: 1,       // B - Артикул
  NAME: 2,          // C - Наименование
  PART_TYPE: 3,     // D - Тип детали
  MATERIAL: 4,      // E - Материал
  LENGTH: 5,        // F - Длина, мм
  WIDTH: 6,         // G - Ширина, мм
  MATERIAL_QTY: 7,  // H - Количество материала
  UNIT: 8,          // I - Ед. изм.
  OP1: 9,           // J - Операция 1
  OP2: 10,          // K - Операция 2
  OP3: 11,          // L - Операция 3
  OP4: 12,          // M - Операция 4
  CROSS_SECTION: 13, // N - Площадь поперечного сечения, cм2
  CUTTING_CONTOUR: 14, // O - Длина контура реза, м
  BENDS: 15,          // P - Кол-во гибов
  WELD_LENGTH: 16,    // Q - Длина сварных швов, мм
  SURFACE: 17,        // R - Площадь поверхности, м2
  MASS: 18,           // S - Масса, кг
  QTY: 19,            // T - К-ВО
} as const;

type RawRow = (string | number | boolean | null | undefined)[];

function str(v: unknown): string {
  if (v == null) return '';
  return String(v).trim();
}

function num(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return isNaN(n) ? null : n;
}

// "1.2.3" → 3, "1" → 1
function getLevel(number: string): number {
  return number.split('.').length;
}

function parseDataRow(raw: RawRow): SpecRow | null {
  const number = str(raw[COL.NUMBER]);
  // Only rows whose № starts with a digit are data rows
  if (!number || !/^\d/.test(number)) return null;

  return {
    number,
    article: str(raw[COL.ARTICLE]),
    name: str(raw[COL.NAME]),
    partType: str(raw[COL.PART_TYPE]),
    material: str(raw[COL.MATERIAL]),
    length: num(raw[COL.LENGTH]),
    width: num(raw[COL.WIDTH]),
    materialQty: num(raw[COL.MATERIAL_QTY]),
    unit: str(raw[COL.UNIT]),
    op1: str(raw[COL.OP1]),
    op2: str(raw[COL.OP2]),
    op3: str(raw[COL.OP3]),
    op4: str(raw[COL.OP4]),
    crossSection: num(raw[COL.CROSS_SECTION]),
    cuttingContour: num(raw[COL.CUTTING_CONTOUR]),
    bends: num(raw[COL.BENDS]),
    weldLength: num(raw[COL.WELD_LENGTH]),
    surface: num(raw[COL.SURFACE]),
    mass: num(raw[COL.MASS]),
    qty: num(raw[COL.QTY]) ?? 1,
    level: getLevel(number),
  };
}

// Computes "Данные для раскроя": product of К-ВО from each row up to the root.
// kvoStack[i] holds the К-ВО of the ancestor at (i+1)-th level.
function buildCuttingData(rows: SpecRow[]): CuttingDataRow[] {
  const result: CuttingDataRow[] = [];
  const kvoStack: number[] = [];
  let idx = 1;

  for (const row of rows) {
    const { level, qty, material, unit, materialQty, partType } = row;

    kvoStack.length = level;
    kvoStack[level - 1] = qty;

    if (material && unit && materialQty != null) {
      const totalQty = kvoStack.reduce((a, b) => a * b, 1);
      const isSheet = unit === 'м2';
      result.push({
        index: idx++,
        name: row.name,
        material,
        partType,
        materialQty,
        unit,
        totalQty,
        partLength: isSheet ? row.length : null,
        partWidth:  isSheet ? row.width  : null,
      });
    }
  }

  return result;
}

export function parseSpecSheet(buffer: ArrayBuffer): ParseResult {
  let wb: XLSX.WorkBook;
  try {
    wb = XLSX.read(buffer, { type: 'array' });
  } catch {
    return { ok: false, error: { kind: 'not_xlsx' } };
  }

  if (wb.SheetNames.length === 0) {
    return { ok: false, error: { kind: 'no_sheet', available: [] } };
  }

  const ws = wb.Sheets[wb.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json<RawRow>(ws, { header: 1, defval: null, raw: true });

  if (raw.length < 2) {
    return { ok: false, error: { kind: 'empty' } };
  }

  // Row 0 is the header; validate that columns A and T match expected names
  const header = raw[0];
  const colA = str(header?.[COL.NUMBER]);
  const colT = str(header?.[COL.QTY]);
  if (colA !== '№' || colT !== 'К-ВО') {
    return { ok: false, error: { kind: 'invalid_header' } };
  }

  const rows: SpecRow[] = [];
  for (let i = 1; i < raw.length; i++) {
    const row = parseDataRow(raw[i]);
    if (row) rows.push(row);
  }

  if (rows.length === 0) {
    return { ok: false, error: { kind: 'empty' } };
  }

  return { ok: true, data: { rows, cuttingData: buildCuttingData(rows) } };
}
